import Button from '@ui/Button'
import Card from '@ui/Card'
import IconButton from '@ui/IconButton'
import { IconSettings } from '@ui/icons'
import Pill from '@ui/Pill'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSectionGroup } from './SectionGroup'

/**
 * The settings-section pattern (design.md §8): a Card with a title, a gear
 * that opens the editor, and a lock Pill when editing is not allowed.
 * Lifted from EventSettings for reuse by the poker/crew sections — one
 * pattern, one place.
 *
 * The gear means "change this" regardless of what opens: pass `renderForm`
 * for an inline form (scalar fields), or `onEdit` for a Sheet (collections
 * and long editors). Inside a labelled `SectionGroup` the title demotes to
 * h3 and the group owns the lock message.
 */
export const SectionShell = ({
  titleKey,
  locked,
  lockedReasonKey,
  renderDisplay,
  renderForm,
  onEdit,
}: {
  titleKey: string
  locked: boolean
  // null = silent lock: the edit affordance disappears without a pill —
  // for fields that are simply over (e.g. seats-per-table once the poker
  // phase is FINISHED), where an explainer would be noise.
  lockedReasonKey?: string | null
  renderDisplay: () => ReactNode
  renderForm?: (onDone: () => void) => ReactNode
  onEdit?: () => void
}) => {
  const { t } = useTranslation()
  const { headingLevel, groupLocked } = useSectionGroup()
  const [editing, setEditing] = useState(false)
  // Bail out of edit mode if the section becomes locked while editing.
  useEffect(() => {
    if (locked && editing) setEditing(false)
  }, [locked, editing])

  const Heading = headingLevel
  // A locked group already says why on its heading — don't repeat it per card.
  const showPill = locked && lockedReasonKey !== null && !groupLocked
  const showGear = !locked && !editing && (renderForm != null || onEdit != null)

  return (
    <Card padding="md" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Heading className="font-display font-bold text-base text-ink-900">
          {t(titleKey)}
        </Heading>
        {showPill && (
          <Pill tone="neutral">
            {lockedReasonKey ? t(lockedReasonKey) : t('events.settings.locked')}
          </Pill>
        )}
        {showGear && (
          <IconButton
            size="sm"
            aria-label={t('events.settings.edit')}
            onClick={() => (onEdit ? onEdit() : setEditing(true))}
          >
            <IconSettings size={16} />
          </IconButton>
        )}
      </div>
      {editing && !locked && renderForm
        ? renderForm(() => setEditing(false))
        : renderDisplay()}
    </Card>
  )
}

export const FormShell = ({
  onSave,
  loading,
  error,
  onCancel,
  children,
}: {
  onSave: () => Promise<void> | void
  loading: boolean
  error: string | null
  onCancel: () => void
  children: ReactNode
}) => {
  const { t } = useTranslation()
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        Promise.resolve(onSave()).catch(() => {
          /* surfaced via error state */
        })
      }}
    >
      {children}
      {error && <p className="text-sm text-flag">{error}</p>}
      <div className="flex gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export const DisplayRow = ({
  labelKey,
  value,
  mono,
}: {
  labelKey: string
  value: string
  mono?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-ink-100 last:border-0">
      <span className="text-xs text-ink-500">{t(labelKey)}</span>
      <span
        className={`text-sm text-ink-900 text-right ${
          mono ? 'font-mono tabular-nums' : ''
        }`}
      >
        {value}
      </span>
    </div>
  )
}
